import { AnimatePresence, motion } from "framer-motion"
import { useState } from "react"
import { RiAddLine, RiSubtractLine } from "@remixicon/react"

import { cn } from "@/lib/utils"

import { easeOut } from "./marketing-motion"

type FaqItem = { q: string; a: string }

type FaqAccordionProps = {
  items: readonly FaqItem[]
  className?: string
  variant?: "default" | "onDark"
}

export function FaqAccordion({ items, className, variant = "default" }: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const onDark = variant === "onDark"

  return (
    <div className={cn(onDark ? "divide-y divide-white/20 border-y border-white/20" : "space-y-2", className)}>
      {items.map((item, i) => {
        const open = openIndex === i
        return (
          <div
            key={item.q}
            className={cn(
              onDark
                ? "border-0 bg-transparent"
                : "rounded-2xl border border-border bg-card transition-colors",
            )}
          >
            <button
              type="button"
              className={cn(
                "flex w-full items-center justify-between gap-4 text-left",
                onDark
                  ? "px-0 py-6 text-white sm:py-7"
                  : "px-5 py-4 text-foreground sm:px-6 sm:py-5",
              )}
              onClick={() => setOpenIndex(open ? null : i)}
              aria-expanded={open}
            >
              <span className="font-heading text-lg sm:text-xl">{item.q}</span>
              <span
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-full",
                  onDark ? "bg-white/10 text-white" : "bg-muted",
                )}
              >
                {open ? (
                  <RiSubtractLine className="size-4" />
                ) : (
                  <RiAddLine className="size-4" />
                )}
              </span>
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: easeOut }}
                  className="overflow-hidden"
                >
                  <p
                    className={cn(
                      "text-sm font-medium leading-relaxed sm:text-base",
                      onDark
                        ? "px-0 pb-6 text-white/80 sm:pb-7"
                        : "px-5 pb-5 text-muted-foreground sm:px-6 sm:pb-6",
                    )}
                  >
                    {item.a}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
